import React from 'react';
import type {
  Control,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useFieldWarningEvaluation } from '../../form/FieldWarningEvaluationContext';
import useFormMethodsContext from '../../form/useFormMethodsContext';

interface FieldProps {
  name: string;
  error?: boolean;
  helperText?: React.ReactNode;
  hideFieldErrorMessage?: boolean;
  onBlur?: (e: unknown) => void;
  onValueChange?: () => void;
  [key: string]: unknown;
}

function createField(
  // biome-ignore lint/suspicious/noExplicitAny: wrapped input components have intentionally heterogeneous props
  Component: React.ComponentType<any>,
  options: { commitWarningOnChange?: boolean } = {},
) {
  function Wrapper(props: FieldProps) {
    const {
      name,
      error = false,
      helperText,
      hideFieldErrorMessage = false,
      onValueChange,
      ...restProps
    } = props;
    const { control } = useFormMethodsContext() as {
      control: Control<FieldValues>;
    };
    const warningEvaluation = useFieldWarningEvaluation();

    const renderField = ({
      field,
      fieldState,
    }: {
      field: ControllerRenderProps;
      fieldState: ControllerFieldState;
    }) => {
      const handleOnBlur = (e: unknown) => {
        field.onBlur();
        const { onBlur } = restProps;
        if (onBlur) {
          onBlur(e);
        }
        warningEvaluation?.commitValue(name);
      };

      const handleOnChange = (...args: Parameters<typeof field.onChange>) => {
        field.onChange(...args);
        onValueChange?.();
        warningEvaluation?.markValueStale(name);
        if (options.commitWarningOnChange || restProps.select === true) {
          warningEvaluation?.commitValue(name);
        }
      };

      const props = {
        error: Boolean(fieldState.error) || error,
        helperText: hideFieldErrorMessage
          ? helperText
          : fieldState.error?.message || helperText,
        ...restProps,
        ...field,
        onBlur: handleOnBlur,
        onChange: handleOnChange,
      };

      return React.createElement(Component, props);
    };

    return <Controller name={name} control={control} render={renderField} />;
  }

  Wrapper.displayName = `${Component.displayName}(Generic)`;
  return Wrapper;
}

export default createField;
